import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MdsIcon } from '@mds/icons';
import { HostnameService } from './hostname.service';

export enum EPDRowActionMenuKeyEnum {
  ChangeStatus = 'status',
  InitializeEPD = 'initialize_epd',
  ChangeVerifier = 'internal_verifier',
  OpenLCA = 'open_lca',
  Publish = 'publish',
  SeeHistory = 'changes_history',
  DownloadArtifacts = 'download_latest_handout',
  ResumeEPD = 'resume_epd'
}

export interface EPDManagementResultItem {
  status: string;
  rid: string;
  name: string;
  level: number;
  family_rid: string;
  products_included: string[];
  is_reference: boolean;
  reference_product: string;
  type: string;
  menu_actions: { [key: string]: boolean };
  menuOptions?: any[];
  actions: { [key: string]: boolean };
  tag?: {
    text: string;
    icon: MdsIcon;
    type: string;
  };
  parents: string[];
  children: EPDManagementResultItem[];
  lc_model_rid: string;
  change_extrapolated_epd: boolean;
  pcr_rid: string;
  prop_PN: string;
  author_name: string;
  latest_epd_handout_download_link: string;
  external_verifier: string;
  version_major: number;
  version_minor: number;
  latest_epd_family_user_log: string;
}

export interface EPDMenuSection {
  description: string;
  field: string;
  formSchema: any[];
  icon: MdsIcon;
  key: EPDRowActionMenuKeyEnum;
  lines: any[];
  name: string;
  options: any[];
  sortlevel: number;
}

export interface EPDRowActionSection {
  description: string;
  key: EPDRowActionMenuKeyEnum;
  name: string;
  options: { name: string; value: string }[];
  field: string;
}

export interface EPDMenuGroup {
  key: string;
  name: string;
  sections: EPDMenuSection[];
}

export interface EPDMenus {
  taxonomy_family: EPDMenuGroup[];
}

export interface RowActions {
  internal_verifier: EPDMenuSection;
  status: EPDRowActionSection;
}

export interface EPDManagementResults {
  data: EPDManagementResultItem[];
  formSchema: {
    menus: EPDMenus;
    row_actions: RowActions;
  };
}

export interface EPDManagementResult {
  data: EPDManagementResultItem;
  formSchema: {
    menus: EPDMenus;
    row_actions: RowActions;
  };
}

export interface UpdateStatusResponse {
  rid: string;
  updated: {
    status: string;
    updated_on: string;
    edges_updated: any[];
  };
}

export interface UpdateExtrapolateEPDResponse {
  rid: string;
  updated: {
    updated_on: string;
    edges_updated: string[];
  };
}

export interface UpdateVerifierResponse {
  rid: string;
  updated: {
    updated_on: string;
    edges_updated: string[];
  };
}

export interface InitializePayload {
  pcr_rid: string;
  update_type: string;
  languages: string[];
  is_external: boolean;
  epd_version?: 'minor' | 'major';
  epd_usage?: 'external' | 'internal';
  regenerate_lc_models: boolean;
  internal_verifier: string;
  external_verifier: string;
  standard: string;
  comment: string;
}

@Injectable({ providedIn: 'root' })
export class EPDManagementService {
  baseUrl: string;

  constructor(private httpClient: HttpClient, private hostnameService: HostnameService) {
    this.baseUrl = this.hostnameService.getOrigin();
  }

  getEPDManagementResults() {
    return this.httpClient.get<EPDManagementResults>(`${this.baseUrl}/api/v1/epd-management`);
  }

  updateStatus(familyRid: string, newStatus: string, date?: string) {
    const payload: { status: string; externally_verified_date?: string } = {
      status: newStatus
    };

    if (date) {
      payload.externally_verified_date = date;
    }

    return this.httpClient.put<UpdateStatusResponse>(
      `${this.baseUrl}/api/v1/composer/route/${familyRid.replace('#', '')}`,
      payload
    );
  }

  updateExtrapolateEPD(familyRid: string, productGroupRid: string, newState: boolean) {
    return this.httpClient.put<UpdateExtrapolateEPDResponse>(
      `${this.baseUrl}/api/v1/composer/route/${familyRid.replace('#', '')}`,
      {
        product_group_rid: productGroupRid,
        extrapolate_epd: newState
      }
    );
  }

  updateVerifier(familyRid: string, verifierRid: string) {
    return this.httpClient.put<UpdateVerifierResponse>(
      `${this.baseUrl}/api/v1/composer/route/${familyRid.replace('#', '')}`,
      {
        internal_verifier_rid: verifierRid
      }
    );
  }

  initializeEPD(familyRid: string, payload: InitializePayload) {
    return this.httpClient.post<{ message: string }>(
      `${this.baseUrl}/api/v1/epd-management/${familyRid.replace('#', '')}/initialize-epd`,
      payload
    );
  }

  publish(familyRid: string) {
    return this.httpClient.post<{ message: string }>(
      `${this.baseUrl}/api/v1/epd-management/${familyRid.replace('#', '')}/publish`,
      null
    );
  }

  resume(familyRid: string, regenerateModels: 'Yes' | 'No') {
    return this.httpClient.post<{ message: string }>(
      `${this.baseUrl}/api/v1/epd-management/${familyRid.replace('#', '')}/resume-epd`,
      {
        regenerate_lc_models: regenerateModels === 'Yes'
      }
    );
  }

  fetchEPDFamilyStatus(familyRid) {
    return this.httpClient.get<EPDManagementResult>(`/api/v1/epd-management/${familyRid}/status`);
  }

  fetchArtifactDownloadLink(url) {
    return this.httpClient.get<{ url: string }>(url);
  }

  fetchChildren(familyRid: string) {
    return this.httpClient.get<EPDManagementResults>(
      `${this.baseUrl}/api/v1/epd-management/${familyRid.replace('#', '')}/expand`
    );
  }

  fetchResultsByPartNumber(searchTerm: string) {
    return this.httpClient.get<EPDManagementResults>(
      `${this.baseUrl}/api/v1/epd-management/search?partNumber=${searchTerm}`
    );
  }
}
